"use client";

import Link from "next/link";
import { useLocale, useTranslations } from "use-intl";
import { navLabel } from "@/lib/i18n/nav-labels";
import { Icon } from "@/components/counter";
import { cn } from "@/lib/utils";
import type { RailItem } from "@/components/shell/PosRail";
import { LanguageQuickToggle } from "@/components/shell/LanguageQuickToggle";

/**
 * The counter's nav on a phone, where the rail cannot go.
 *
 * <p>{@link PosShell} is a three-column frame: a 72px rail, the workspace, and a 384px ledger. On a
 * 390px phone the rail and the ledger alone want 456px, so the workspace was squeezed to nothing —
 * measured at 606px viewport, the rail took 355px of it and the page still scrolled sideways. Every
 * POS screen in every vertical mounts into that frame, so this is one fix for all of them.
 *
 * <p>Bottom rather than a hamburger because this is a till: it is used one-handed, often in a hurry,
 * and the thumb is at the bottom of the phone. It is the same nav the rail shows, in the same order,
 * so nobody has to learn a second layout.
 *
 * <p>Five items at most. A tab bar that scrolls is a tab bar nobody reads to the end of, and the
 * sixth item onward is back-office work that a phone at a counter is not the place for — it stays
 * reachable from Settings and from the rail on anything wider.
 */
export function PosBottomTabs({ items, active }: { items: RailItem[]; active?: string }) {
  const t = useTranslations("rail");
  const locale = useLocale();
  const tabs = items.filter((item) => item.href).slice(0, 5);
  if (tabs.length === 0) return null;

  return (
    <nav
      data-testid="pos-bottom-tabs"
      aria-label={t("primary")}
      className={cn(
        "flex shrink-0 items-stretch justify-around border-t border-stroke-on-rail/40 bg-bg-rail text-text-on-rail md:hidden",
        // The home indicator on a modern phone sits over the last few pixels of the viewport.
        "pb-[env(safe-area-inset-bottom)]",
      )}
    >
      {tabs.map((tab) => {
        const current = active === tab.id;
        return (
          <Link
            key={tab.id}
            href={tab.href!}
            aria-current={current ? "page" : undefined}
            className={cn(
              // 56px tall and an equal share of the width: comfortably past the 44px minimum a
              // finger needs, which a 72px-wide icon rail squeezed into a phone never was.
              // min-w-0: a flex item's default min-width is `auto`, which is the label's own
              // width, so `flex-1` could not shrink these below their text and the bar ran
              // 111px past a 400px phone. The label truncates; this lets it.
              "flex min-h-[56px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-sm leading-tight",
              current ? "text-text-on-fill" : "text-text-on-rail/70",
            )}
          >
            <Icon name={tab.icon ?? "orders"} className="h-[18px] w-[18px]" />
            <span className="w-full truncate text-center">{navLabel(tab.label, locale)}</span>
          </Link>
        );
      })}
      {/* The rail's language switch, which a phone never sees. */}
      <LanguageQuickToggle variant="tab" />
    </nav>
  );
}
